/**
 * Key input tabs component for DEX reader workflow.
 * Switches between File upload and Text paste modes for private key input.
 * Specialized for key input: reads file as text (private keys are base64).
 */

/**
 * Render key input tabs into a container.
 * @param {HTMLElement} container - Container element
 * @param {Object} state - Component state
 * @param {string} [state.activeTab='file'] - 'file' or 'text'
 * @param {File|null} [state.file] - Selected file
 * @param {string|null} [state.fileName] - File name display
 * @param {string|null} [state.fileError] - File error message
 * @param {string} [state.textContent=''] - Text content (for text tab)
 * @param {string|null} [state.textError] - Text error message
 * @param {boolean} [state.disabled=false] - Whether inputs are disabled
 */
export function renderKeyInputTabs(container, state = {}) {
  const {
    activeTab = 'file',
    file = null,
    fileName = null,
    fileError = null,
    textContent = '',
    textError = null,
    disabled = false
  } = state;

  const isFileActive = activeTab === 'file';
  const isTextActive = activeTab === 'text';

  // Tab styles
  const activeTabClasses = 'border-b-2 border-blue-500 text-blue-600';
  const inactiveTabClasses = 'text-gray-500 hover:text-gray-700';

  // Disabled classes
  const disabledClasses = disabled
    ? 'opacity-50 cursor-not-allowed pointer-events-none'
    : 'cursor-pointer hover:border-blue-500';

  container.innerHTML = `
    <div>
      <div class="border-b border-gray-200 mb-4">
        <nav class="flex -mb-px">
          <button
            type="button"
            data-key-tab="file"
            class="px-4 py-2 text-sm font-medium ${isFileActive ? activeTabClasses : inactiveTabClasses}"
            ${disabled ? 'disabled' : ''}
          >
            File
          </button>
          <button
            type="button"
            data-key-tab="text"
            class="px-4 py-2 text-sm font-medium ${isTextActive ? activeTabClasses : inactiveTabClasses}"
            ${disabled ? 'disabled' : ''}
          >
            Text
          </button>
        </nav>
      </div>
      <div id="key-tab-content"></div>
    </div>
  `;

  // Render tab content
  const tabContent = container.querySelector('#key-tab-content');

  if (isFileActive) {
    // Render file drop zone for key file
    const displayName = fileName || (file ? file.name : null);

    tabContent.innerHTML = `
      <div
        id="key-file-drop-zone"
        class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center transition-colors ${disabledClasses}"
      >
        <input type="file" id="key-file-input" class="hidden" ${disabled ? 'disabled' : ''} accept=".txt,.pem,.key" />

        ${file ? `
          <div class="flex items-center justify-center gap-3">
            <svg class="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"></path>
            </svg>
            <div class="text-left">
              <p class="text-sm font-medium text-gray-700 truncate max-w-xs">${displayName}</p>
              <p class="text-xs text-gray-500">Private key file</p>
            </div>
            <button
              type="button"
              id="key-file-clear-btn"
              class="ml-2 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
              aria-label="Remove file"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        ` : `
          <svg class="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"></path>
          </svg>
          <p class="mt-2 text-sm text-gray-600">Drag and drop your private key file</p>
          <p class="text-xs text-gray-400">or click to browse</p>
        `}
      </div>
      ${fileError ? `<p class="text-red-500 text-xs mt-1">${fileError}</p>` : ''}
    `;
  } else {
    // Render text input for pasting key
    const disabledAttr = disabled ? 'disabled' : '';
    const errorHtml = textError
      ? `<p class="text-red-500 text-xs mt-1">${textError}</p>`
      : '';

    tabContent.innerHTML = `
      <div>
        <textarea
          id="key-text-content"
          rows="4"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 resize-none font-mono text-sm"
          placeholder="Paste your private key (base64)..."
          ${disabledAttr}
        >${escapeHtml(textContent)}</textarea>
        <p class="text-gray-400 text-xs mt-1">44 characters, base64 encoded</p>
        ${errorHtml}
      </div>
    `;
  }
}

/**
 * Initialize key input tabs with event handlers.
 * @param {HTMLElement} container - Container element
 * @param {Object} callbacks - Callback functions
 * @param {Function} [callbacks.onTabChange] - Called when tab changes: (tab) => void
 * @param {Function} [callbacks.onFileChange] - Called when file read: (keyContent, error) => void
 * @param {Function} [callbacks.onFileClear] - Called when file cleared: () => void
 * @param {Function} [callbacks.onTextChange] - Called when text changes: (text) => void
 * @param {Object} [state] - Current state (needed to know which tab is active)
 * @returns {Function} Cleanup function that removes event listeners
 */
export function initKeyInputTabs(container, callbacks = {}, state = {}) {
  const { onTabChange, onFileChange, onFileClear, onTextChange } = callbacks;
  const { activeTab = 'file' } = state;

  const cleanupFunctions = [];

  // Wire tab button clicks
  const tabButtons = container.querySelectorAll('[data-key-tab]');
  function handleTabClick(e) {
    const tab = e.target.dataset.keyTab;
    if (tab && onTabChange) {
      onTabChange(tab);
    }
  }

  tabButtons.forEach((btn) => {
    btn.addEventListener('click', handleTabClick);
  });

  cleanupFunctions.push(() => {
    tabButtons.forEach((btn) => {
      btn.removeEventListener('click', handleTabClick);
    });
  });

  // Wire content based on active tab
  if (activeTab === 'file') {
    // Initialize file drop zone for key
    const dropZone = container.querySelector('#key-file-drop-zone');
    const fileInput = container.querySelector('#key-file-input');
    const clearBtn = container.querySelector('#key-file-clear-btn');

    if (dropZone && fileInput) {
      // Click to browse
      function handleClick(e) {
        if (e.target.closest('#key-file-clear-btn')) return;
        fileInput.click();
      }

      // File selected
      function handleFileSelect(e) {
        const file = e.target.files?.[0];
        if (file) {
          readKeyFile(file);
        }
      }

      // Drag events
      function handleDragEnter(e) {
        e.preventDefault();
        dropZone.classList.add('border-blue-500', 'bg-blue-50');
      }

      function handleDragOver(e) {
        e.preventDefault();
        dropZone.classList.add('border-blue-500', 'bg-blue-50');
      }

      function handleDragLeave(e) {
        e.preventDefault();
        dropZone.classList.remove('border-blue-500', 'bg-blue-50');
      }

      function handleDrop(e) {
        e.preventDefault();
        dropZone.classList.remove('border-blue-500', 'bg-blue-50');
        const file = e.dataTransfer?.files?.[0];
        if (file) {
          readKeyFile(file);
        }
      }

      // Read key file as text (keys are base64 text)
      function readKeyFile(file) {
        const reader = new FileReader();
        reader.onload = () => {
          const content = reader.result.trim();
          if (onFileChange) {
            onFileChange(content, null, file);
          }
        };
        reader.onerror = () => {
          if (onFileChange) {
            onFileChange(null, 'Failed to read key file', null);
          }
        };
        reader.readAsText(file);
      }

      // Clear button
      function handleClear() {
        fileInput.value = '';
        if (onFileClear) {
          onFileClear();
        }
      }

      // Attach listeners
      dropZone.addEventListener('click', handleClick);
      fileInput.addEventListener('change', handleFileSelect);
      dropZone.addEventListener('dragenter', handleDragEnter);
      dropZone.addEventListener('dragover', handleDragOver);
      dropZone.addEventListener('dragleave', handleDragLeave);
      dropZone.addEventListener('drop', handleDrop);

      if (clearBtn) {
        clearBtn.addEventListener('click', handleClear);
      }

      cleanupFunctions.push(() => {
        dropZone.removeEventListener('click', handleClick);
        fileInput.removeEventListener('change', handleFileSelect);
        dropZone.removeEventListener('dragenter', handleDragEnter);
        dropZone.removeEventListener('dragover', handleDragOver);
        dropZone.removeEventListener('dragleave', handleDragLeave);
        dropZone.removeEventListener('drop', handleDrop);
        if (clearBtn) {
          clearBtn.removeEventListener('click', handleClear);
        }
      });
    }
  } else {
    // Wire textarea blur (not input - avoids re-render on every keystroke)
    const textarea = container.querySelector('#key-text-content');
    if (textarea) {
      function handleTextBlur(e) {
        if (onTextChange) {
          onTextChange(e.target.value);
        }
      }
      textarea.addEventListener('blur', handleTextBlur);
      cleanupFunctions.push(() => {
        textarea.removeEventListener('blur', handleTextBlur);
      });
    }
  }

  // Return combined cleanup function
  return function cleanup() {
    cleanupFunctions.forEach((fn) => fn());
  };
}

/**
 * Escape HTML special characters to prevent XSS.
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
function escapeHtml(str) {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
